import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, ScanSearch, Upload, Trash2, Download, Pencil, X, Check, AlertTriangle, Camera, PlayCircle, FileSpreadsheet, Database } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiErrorDetail } from "@/lib/api";

const Mono = ({ children, className = "", ...rest }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`} {...rest}>{children}</span>
);

const money = (n) => (n == null ? "—" : n.toLocaleString("en-AU", { style: "currency", currency: "AUD" }));
const SEV = ["", "Minor", "Low", "Moderate", "Severe", "Critical"];

const STATUS_STYLE = {
  verified: "text-emerald-300 border-emerald-400/40",
  review: "text-amber-300 border-amber-400/40",
  no_evidence: "text-red-300 border-red-400/40",
  unmapped: "text-white/40 border-white/20",
};
const STATUS_LABEL = { verified: "Verified", review: "Review — AI disagrees", no_evidence: "No evidence", unmapped: "Unmapped" };

function RadarImportPanel({ jur, region, onScanned }) {
  const [manifest, setManifest] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState("");
  const csvRef = useRef(null);
  const photoRef = useRef(null);

  const parseCsv = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/ew/radar_csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setManifest(data);
      toast.success(`${data.count} RADAR requests parsed from ${data.filename}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "RADAR file parse failed");
    } finally {
      setBusy(false);
      if (csvRef.current) csvRef.current.value = "";
    }
  };

  const matchRow = (filename) => {
    const fn = filename.toUpperCase();
    return (manifest?.requests || []).find((r) => {
      const rid = (r.request_id || "").toUpperCase();
      const digits = rid.replace(/\D/g, "");
      return (rid.length > 3 && fn.includes(rid)) || (digits.length > 4 && fn.includes(digits));
    });
  };

  const matched = photos.filter((f) => matchRow(f.name));
  const unmatched = photos.filter((f) => !matchRow(f.name));

  const shrinkToBlob = (file) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      if (scale === 1 && file.size < 1_500_000) return resolve(file);
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      c.toBlob((b) => resolve(b || file), "image/jpeg", 0.82);
    };
    img.onerror = () => resolve(file);
    img.src = URL.createObjectURL(file);
  });

  const scanAll = async () => {
    setScanning(true);
    let ok = 0, fail = 0, done = 0;
    const queue = [...matched];
    const worker = async () => {
      while (queue.length) {
        const f = queue.shift();
        const row = matchRow(f.name);
        try {
          const blob = await shrinkToBlob(f);
          const fd = new FormData();
          fd.append("file", blob, f.name);
          fd.append("road", [row.road_id, row.road_name].filter(Boolean).join(" "));
          fd.append("radar_ref", row.road_id ? `${row.request_id}_${row.road_id}` : row.request_id);
          const len = row.ch_start != null && row.ch_end != null ? Math.max(Math.round(Math.abs(row.ch_end - row.ch_start) * 1000), 5) : 50;
          fd.append("chainage_m", len);
          fd.append("jurisdiction", jur);
          if (jur === "nsw") fd.append("region", region);
          const { data } = await api.post("/ew/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
          onScanned(data);
          ok++;
        } catch { fail++; }
        done++;
        setProgress(`Scanning ${done}/${matched.length}${fail ? ` · ${fail} failed` : ""}`);
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setScanning(false);
    setProgress("");
    setPhotos([]);
    if (photoRef.current) photoRef.current.value = "";
    toast[fail ? "warning" : "success"](`${ok} photo${ok === 1 ? "" : "s"} scanned against RADAR requests${fail ? ` · ${fail} failed` : ""}`);
  };

  return (
    <div className="border border-gold-500/20 bg-navy-900/40 p-5" data-testid="ew-radar-panel">
      <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2 mb-2"><FileSpreadsheet className="h-3.5 w-3.5" /> RADAR bulk import — one road, all photos</Mono>
      <p className="text-white/40 text-xs font-light mb-3">1 — upload the RADAR request export (csv/xlsx). 2 — drop the photos from the RADAR folders; each photo pairs to its request by the GR number in the filename and scans with road, ref and chainage pre-filled.</p>
      <div className="flex items-center gap-2 flex-wrap">
        <label className={`inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 px-4 py-2 text-xs hover:bg-gold-500/10 cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} {manifest ? `RADAR file · ${manifest.count} requests` : "1 · RADAR export (csv/xlsx)"}
          <input ref={csvRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={parseCsv} data-testid="ew-radar-csv-input" />
        </label>
        <label className={`inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 px-4 py-2 text-xs hover:bg-gold-500/10 cursor-pointer ${!manifest ? "opacity-40 pointer-events-none" : ""}`}>
          <Camera className="h-3.5 w-3.5" /> {photos.length ? `${photos.length} photos chosen` : "2 · Choose RADAR photos"}
          <input ref={photoRef} type="file" accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif" multiple className="hidden"
            onChange={(e) => setPhotos(Array.from(e.target.files || []))} data-testid="ew-radar-photos-input" />
        </label>
        {photos.length > 0 && (
          <>
            <span className="text-xs font-mono text-emerald-300" data-testid="ew-radar-matched">{matched.length} matched</span>
            {unmatched.length > 0 && <span className="text-xs font-mono text-amber-300" title={unmatched.map((f) => f.name).join(", ")} data-testid="ew-radar-unmatched">{unmatched.length} no GR match</span>}
            <button onClick={scanAll} disabled={scanning || !matched.length} data-testid="ew-radar-scan-btn"
              className="inline-flex items-center gap-2 bg-gold-500 text-navy-950 font-semibold px-4 py-2 text-xs hover:bg-gold-400 disabled:opacity-50">
              {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ScanSearch className="h-3.5 w-3.5" />} Scan {matched.length} matched
            </button>
          </>
        )}
        {progress && <span className="text-xs text-white/50 font-mono" data-testid="ew-radar-progress">{progress}</span>}
      </div>
    </div>
  );
}

function SheetValidatePanel() {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const sheetRef = useRef(null);
  const ingest = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/ew/ingest_sheet", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      toast.success(`${data.summary.total} rows ingested from "${data.sheet}" — ${data.summary.verified} verified, ${data.summary.no_evidence} without evidence`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Sheet ingest failed");
    } finally {
      setBusy(false);
      if (sheetRef.current) sheetRef.current.value = "";
    }
  };
  const exportDataset = () => {
    const t = localStorage.getItem("mdau_token");
    window.open(`${API}/ew/dataset/export?token=${t}`, "_blank");
  };
  return (
    <div className="border border-gold-500/20 bg-navy-900/40 p-5" data-testid="ew-sheet-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2"><FileSpreadsheet className="h-3.5 w-3.5" /> Assessment sheet validation &amp; training data</Mono>
        <div className="flex items-center gap-2">
          <label className={`inline-flex items-center gap-2 border border-gold-500/40 text-gold-300 px-4 py-2 text-xs hover:bg-gold-500/10 cursor-pointer ${busy ? "opacity-50 pointer-events-none" : ""}`}>
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Validate sheet (REPA / EW xlsx)
            <input ref={sheetRef} type="file" accept=".xlsx" className="hidden" onChange={ingest} data-testid="ew-sheet-input" />
          </label>
          <button onClick={exportDataset} data-testid="ew-dataset-export-btn"
            className="inline-flex items-center gap-2 border border-sky-400/40 text-sky-300 px-4 py-2 text-xs hover:bg-sky-400/10">
            <Database className="h-3.5 w-3.5" /> Export YOLO dataset
          </button>
        </div>
      </div>
      <p className="text-white/40 text-xs font-light">Ingest an organised EW assessment sheet (DTMR REPA form or equivalent) — every row is checked against AI-scanned photo evidence. The YOLO export banks all scans as RDD-2022-aligned training data.</p>
      {report && (
        <div className="mt-4" data-testid="ew-sheet-report">
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {["verified", "review", "no_evidence", "unmapped"].map((k) => (
              <span key={k} className={`border px-2 py-1 text-[0.65rem] font-mono ${STATUS_STYLE[k]}`} data-testid={`ew-sheet-count-${k}`}>
                {report.summary[k]} {STATUS_LABEL[k]}
              </span>
            ))}
            <span className="text-white/40 text-[0.65rem] font-mono">{report.summary.total} rows · {report.filename}</span>
          </div>
          <div className="overflow-x-auto max-h-96 overflow-y-auto border border-gold-500/10">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-navy-900">
                <tr className="text-left border-b border-gold-500/15">
                  {["#", "Road / Site", "Sheet damage", "Mapped class", "Photo ref", "Chainage", "Photochecker"].map((h) => (
                    <th key={h} className="px-3 py-2"><Mono className="text-[0.5rem] text-white/40">{h}</Mono></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.row} className="border-b border-gold-500/5" data-testid={`ew-sheet-row-${r.row}`}>
                    <td className="px-3 py-1.5 text-white/30 font-mono">{r.row}</td>
                    <td className="px-3 py-1.5 text-white/70 max-w-[220px] truncate">{r.road || r.site || "—"}</td>
                    <td className="px-3 py-1.5 text-white/60 max-w-[200px] truncate" title={r.damage_text}>{r.damage_text}</td>
                    <td className="px-3 py-1.5 text-gold-400">{r.mapped_class ? r.mapped_class.replace(/_/g, " ") : "—"}{r.rdd_code && <span className="text-white/40 ml-1">({r.rdd_code})</span>}</td>
                    <td className="px-3 py-1.5 text-white/50 font-mono">{r.photo_ref || "—"}</td>
                    <td className="px-3 py-1.5 text-white/50 font-mono">{r.ch_start != null ? `${r.ch_start}–${r.ch_end ?? ""}` : "—"}</td>
                    <td className="px-3 py-1.5">
                      <span className={`border px-1.5 py-0.5 text-[0.6rem] font-mono ${STATUS_STYLE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                      {r.status === "review" && r.ai_class && <span className="text-amber-300/70 ml-1.5 text-[0.6rem]">{r.match_via === "road" ? "road-match only" : `AI saw ${r.ai_class.replace(/_/g, " ")}`}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <Mono className="text-[0.5rem] text-white/40 block mb-1">{label}</Mono>
      {children}
    </label>
  );
}

const inputCls = "w-full bg-navy-900 border border-gold-500/20 text-white text-sm px-3 py-2 focus:border-gold-500/60 outline-none";

function UploadPanel({ onScanned, jur, region, setRegion, regions }) {
  const fileRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [road, setRoad] = useState("");
  const [radarRef, setRadarRef] = useState("");
  const [dims, setDims] = useState({ chainage_m: "50", width_m: "3.5", depth_m: "0.1" });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");

  const run = async () => {
    if (!files.length) return toast.error("Choose at least one damage photo");
    setBusy(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setProgress(`Scanning ${i + 1}/${files.length} — ${files[i].name}`);
      const fd = new FormData();
      fd.append("file", files[i]);
      fd.append("road", road);
      fd.append("radar_ref", radarRef);
      fd.append("jurisdiction", jur);
      fd.append("region", region);
      Object.entries(dims).forEach(([k, v]) => fd.append(k, v || "0"));
      try {
        const { data } = await api.post("/ew/scan", fd);
        ok += 1;
        onScanned(data);
      } catch (e) {
        toast.error(`${files[i].name}: ${formatApiErrorDetail(e.response?.data?.detail)}`);
      }
    }
    setBusy(false);
    setProgress("");
    if (ok) {
      toast.success(`${ok} photo${ok > 1 ? "s" : ""} scanned and costed`);
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="border border-gold-500/20 bg-navy-900/40 p-5" data-testid="ew-upload-panel">
      <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2 mb-4"><Upload className="h-3.5 w-3.5" /> Scan damage photos</Mono>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <Field label="Damage photos (JPG/PNG, max 8MB each)">
            <input ref={fileRef} type="file" accept="image/*,.jpg,.jpeg,.png,.webp,.heic,.heif" multiple data-testid="ew-file-input"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="w-full text-sm text-white/70 file:mr-3 file:px-4 file:py-2 file:border-0 file:bg-gold-500 file:text-navy-950 file:font-semibold file:cursor-pointer file:text-xs bg-navy-900 border border-gold-500/20 cursor-pointer" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Road ID & name">
              <input value={road} onChange={(e) => setRoad(e.target.value)} placeholder="e.g. 26E-014 Bee Creek Rd" className={inputCls} data-testid="ew-road-input" />
            </Field>
            <Field label={jur === "nsw" ? "Photo ref" : "RADAR photo ref"}>
              <input value={radarRef} onChange={(e) => setRadarRef(e.target.value)} placeholder="e.g. RDR-000123" className={inputCls} data-testid="ew-radar-input" />
            </Field>
          </div>
          {jur === "nsw" && (
            <Field label="Benchmark region (NSW)">
              <select value={region} onChange={(e) => setRegion(e.target.value)} className={inputCls} data-testid="ew-region-select">
                {regions.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          )}
        </div>
        <div>
          <Mono className="text-[0.5rem] text-white/40 block mb-1">Affected extent (used for quantity & cost — editable per scan later)</Mono>
          <div className="grid grid-cols-3 gap-3">
            {[["chainage_m", "Chainage (m)"], ["width_m", "Width (m)"], ["depth_m", "Depth (m)"]].map(([k, l]) => (
              <Field key={k} label={l}>
                <input type="number" step="0.01" value={dims[k]} onChange={(e) => setDims((d) => ({ ...d, [k]: e.target.value }))} className={inputCls} data-testid={`ew-dim-${k}`} />
              </Field>
            ))}
          </div>
          <button onClick={run} disabled={busy} data-testid="ew-scan-btn"
            className="mt-4 w-full bg-gold-500 text-navy-950 font-semibold text-sm py-2.5 hover:bg-gold-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {progress}</> : <><ScanSearch className="h-4 w-4" /> Run AI damage scan</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportFCModal({ onClose, onScanned, jur, region }) {
  const [claims, setClaims] = useState(null);
  const [claimId, setClaimId] = useState("");
  const [photos, setPhotos] = useState(null);
  const [sel, setSel] = useState([]);
  const [dims, setDims] = useState({ chainage_m: "50", width_m: "3.5", depth_m: "0.1" });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const token = localStorage.getItem("mdau_token");

  useEffect(() => { api.get("/fieldclaim/claims").then((r) => setClaims(r.data)).catch(() => setClaims([])); }, []);
  useEffect(() => {
    if (!claimId) return;
    setPhotos(null); setSel([]);
    api.get(`/ew/fc_photos?claim_id=${claimId}`).then((r) => setPhotos(r.data)).catch(() => setPhotos([]));
  }, [claimId]);

  const run = async () => {
    setBusy(true);
    let ok = 0;
    for (let i = 0; i < sel.length; i++) {
      const p = photos.find((x) => x.id === sel[i]);
      setProgress(`Scanning ${i + 1}/${sel.length} — ${p?.original_filename || sel[i]}`);
      try {
        const { data } = await api.post("/ew/scan_fc", {
          photo_id: sel[i], chainage_m: +dims.chainage_m, width_m: +dims.width_m, depth_m: +dims.depth_m,
          jurisdiction: jur, region: jur === "nsw" ? region : null,
        });
        ok += 1;
        onScanned(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }
    setBusy(false);
    if (ok) { toast.success(`${ok} FieldClaim photo${ok > 1 ? "s" : ""} scanned and costed`); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ew-fc-modal">
      <div className="absolute inset-0 bg-navy-950/85" onClick={() => !busy && onClose()} />
      <div className="relative bg-navy-900 border border-gold-500/30 w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gold-500/15">
          <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2"><Camera className="h-3.5 w-3.5" /> Import damage photos from FieldClaim</Mono>
          <button onClick={onClose} disabled={busy} data-testid="ew-fc-close" className="text-white/40 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <Field label="Claim / event">
            <select value={claimId} onChange={(e) => setClaimId(e.target.value)} className={inputCls} data-testid="ew-fc-claim-select">
              <option value="">{claims === null ? "Loading claims…" : "Select a claim…"}</option>
              {(claims || []).map((c) => <option key={c.id} value={c.id}>{c.name} · {c.photo_count} photos</option>)}
            </select>
          </Field>
          {claimId && photos === null && <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gold-400" /></div>}
          {photos && photos.length === 0 && <div className="py-6 text-center text-white/40 text-sm" data-testid="ew-fc-empty">No damage-phase photos on this claim.</div>}
          {photos && photos.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <Mono className="text-[0.5rem] text-white/40">{photos.length} damage photos · road & RADAR ref auto-filled from the asset</Mono>
                <button data-testid="ew-fc-select-all" className="text-gold-400 text-xs hover:text-gold-500"
                  onClick={() => setSel(sel.length === photos.length ? [] : photos.map((p) => p.id))}>
                  {sel.length === photos.length ? "Clear all" : "Select all"}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {photos.map((p) => {
                  const on = sel.includes(p.id);
                  return (
                    <button key={p.id} data-testid={`ew-fc-photo-${p.id}`}
                      onClick={() => setSel((s) => (on ? s.filter((x) => x !== p.id) : [...s, p.id]))}
                      className={`text-left border transition-colors ${on ? "border-gold-500 bg-gold-500/10" : "border-gold-500/15 hover:border-gold-500/40"}`}>
                      <div className="relative">
                        <img src={`${API}/fieldclaim/photos/${p.id}/file?token=${token}`} alt="" loading="lazy" className="w-full h-28 object-cover bg-navy-950" />
                        {on && <span className="absolute top-1.5 right-1.5 bg-gold-500 text-navy-950 rounded-full p-0.5"><Check className="h-3 w-3" /></span>}
                        {p.scanned && <Mono className="absolute bottom-1.5 left-1.5 text-[0.45rem] bg-emerald-500/90 text-navy-950 px-1.5 py-0.5">Scanned</Mono>}
                      </div>
                      <div className="px-2 py-1.5">
                        <div className="text-white text-[0.65rem] truncate">{p.original_filename || p.id.slice(0, 8)}</div>
                        <div className="text-white/40 text-[0.6rem] truncate">{p.asset_name || "—"}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[["chainage_m", "Chainage (m)"], ["width_m", "Width (m)"], ["depth_m", "Depth (m)"]].map(([k, l]) => (
                  <Field key={k} label={l}>
                    <input type="number" step="0.01" value={dims[k]} onChange={(e) => setDims((d) => ({ ...d, [k]: e.target.value }))} className={inputCls} data-testid={`ew-fc-dim-${k}`} />
                  </Field>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gold-500/15">
          <button onClick={run} disabled={busy || !sel.length} data-testid="ew-fc-scan-btn"
            className="w-full bg-gold-500 text-navy-950 font-semibold text-sm py-2.5 hover:bg-gold-400 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {progress}</> : <><ScanSearch className="h-4 w-4" /> Scan {sel.length} selected photo{sel.length === 1 ? "" : "s"}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditRow({ scan, classes, onSave, onCancel }) {
  const [form, setForm] = useState({
    damage_class: scan.scan.damage_class, road: scan.road || "", radar_ref: scan.radar_ref || "",
    chainage_m: scan.chainage_m, width_m: scan.width_m, depth_m: scan.depth_m,
  });
  const save = async () => {
    try {
      const { data } = await api.patch(`/ew/scans/${scan.id}`, { ...form, chainage_m: +form.chainage_m, width_m: +form.width_m, depth_m: +form.depth_m });
      onSave(data);
      toast.success("Scan updated — cost recalculated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <tr className="bg-navy-900/70" data-testid={`ew-edit-row-${scan.id}`}>
      <td colSpan={9} className="px-4 py-3">
        <div className="grid md:grid-cols-6 gap-3 items-end">
          <Field label="Damage class">
            <select value={form.damage_class} onChange={(e) => setForm((f) => ({ ...f, damage_class: e.target.value }))} className={inputCls} data-testid="ew-edit-class">
              {classes.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
            </select>
          </Field>
          <Field label="Road"><input value={form.road} onChange={(e) => setForm((f) => ({ ...f, road: e.target.value }))} className={inputCls} data-testid="ew-edit-road" /></Field>
          <Field label="RADAR ref"><input value={form.radar_ref} onChange={(e) => setForm((f) => ({ ...f, radar_ref: e.target.value }))} className={inputCls} /></Field>
          {[["chainage_m", "Chainage (m)"], ["width_m", "Width (m)"], ["depth_m", "Depth (m)"]].map(([k, l]) => (
            <Field key={k} label={l}><input type="number" step="0.01" value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls} data-testid={`ew-edit-${k}`} /></Field>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={save} data-testid="ew-edit-save" className="bg-gold-500 text-navy-950 text-xs font-semibold px-4 py-1.5 flex items-center gap-1.5 hover:bg-gold-400"><Check className="h-3 w-3" /> Save & recost</button>
          <button onClick={onCancel} data-testid="ew-edit-cancel" className="border border-gold-500/25 text-white/60 text-xs px-4 py-1.5 flex items-center gap-1.5 hover:text-white"><X className="h-3 w-3" /> Cancel</button>
        </div>
      </td>
    </tr>
  );
}

function CompilePanel({ selected, onDone, jur, region, regions }) {
  const [form, setForm] = useState({ district: "Mackay/Whitsunday", event_id: "26E", lga_code: "236", lga_name: "Isaac Regional" });
  const [nswForm, setNswForm] = useState({ agrn: "", event_name: "", council: "" });
  const [busy, setBusy] = useState(false);
  const download = (res, fallback) => {
    const cd = res.headers["content-disposition"] || "";
    const fname = /filename="(.+?)"/.exec(cd)?.[1] || fallback;
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = fname; a.click();
    URL.revokeObjectURL(url);
  };
  const compile = async () => {
    setBusy(true);
    try {
      if (jur === "nsw") {
        const res = await api.post("/ew/nswra_schedule", { ...nswForm, region, scan_ids: selected }, { responseType: "blob" });
        download(res, "NSWRA_EW_Schedule.xlsx");
        toast.success("NSWRA EW schedule compiled and downloaded");
      } else {
        const res = await api.post("/ew/form1", { ...form, scan_ids: selected }, { responseType: "blob" });
        download(res, `E-${form.lga_code}-VAR-${form.event_id}_EW_Form1.xlsx`);
        toast.success("EW Form 1 compiled and downloaded");
      }
      onDone?.();
    } catch (e) {
      const detail = e.response?.data instanceof Blob ? await e.response.data.text().then((t) => { try { return JSON.parse(t).detail; } catch { return t; } }) : e.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail));
    }
    setBusy(false);
  };
  return (
    <div className="border border-gold-500/20 bg-navy-900/40 p-5" data-testid="ew-compile-panel">
      <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2 mb-4">
        <Download className="h-3.5 w-3.5" /> {jur === "nsw" ? "Compile NSWRA EW claim schedule" : "Compile DTMR EW Claim Form 1"}
      </Mono>
      {jur === "nsw" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["agrn", "AGRN"], ["event_name", "Event name"], ["council", "Council"]].map(([k, l]) => (
            <Field key={k} label={l}><input value={nswForm[k]} onChange={(e) => setNswForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls} data-testid={`ew-nsw-${k}`} /></Field>
          ))}
          <Field label="Region"><input value={region} readOnly className={`${inputCls} opacity-60`} data-testid="ew-nsw-region" /></Field>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["district", "DTMR district"], ["event_id", "Event ID"], ["lga_code", "LGA code"], ["lga_name", "LGA name"]].map(([k, l]) => (
            <Field key={k} label={l}><input value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} className={inputCls} data-testid={`ew-form1-${k}`} /></Field>
          ))}
        </div>
      )}
      <button onClick={compile} disabled={busy || !selected.length} data-testid="ew-compile-btn"
        className="mt-4 bg-gold-500 text-navy-950 font-semibold text-sm px-6 py-2.5 hover:bg-gold-400 transition-colors disabled:opacity-40 flex items-center gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {jur === "nsw"
          ? `Compile NSWRA schedule (${selected.length} scan${selected.length === 1 ? "" : "s"})`
          : `Compile Form 1 (${selected.length} scan${selected.length === 1 ? "" : "s"}) — E-${form.lga_code}-VAR-${form.event_id}`}
      </button>
      <p className="text-white/40 text-xs mt-2">
        {jur === "nsw"
          ? "One line per scanned photo, costed against regionally verified rates for the region, SmartyGrants-aligned columns. QLD-costed scans are re-priced against the NSW regional rates on export. Lines without a published regional rate show 'Rate on application'."
          : "Grouped by road, one line item per road. RADAR refs joined into the photo-ref column. Costs include the 10% EW uplift, excl GST."}
      </p>
    </div>
  );
}

export default function EWPrep() {
  const [scans, setScans] = useState(null);
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [fcOpen, setFcOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [jur, setJur] = useState("qld");
  const [region, setRegion] = useState("North Coast");

  useEffect(() => {
    Promise.all([api.get("/ew/scans"), api.get("/ew/meta")])
      .then(([s, m]) => { setScans(s.data); setMeta(m.data); })
      .catch(() => setScans([]));
  }, []);

  const classes = useMemo(() => Object.keys(meta?.damage_classes || {}), [meta]);
  const total = useMemo(() => (scans || []).filter((s) => selected.includes(s.id)).reduce((a, s) => a + (s.cost?.cost_ex_gst || 0), 0), [scans, selected]);

  const onScanned = (doc) => setScans((s) => [doc, ...(s || [])]);
  const onSaved = (doc) => { setScans((s) => s.map((x) => (x.id === doc.id ? doc : x))); setEditing(null); };
  const del = async (id) => {
    await api.delete(`/ew/scans/${id}`).catch(() => {});
    setScans((s) => s.filter((x) => x.id !== id));
    setSelected((sel) => sel.filter((x) => x !== id));
  };
  const toggleSel = (id) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const [sort, setSort] = useState({ key: "created_at", dir: -1 });
  const [roadFilter, setRoadFilter] = useState("");
  const roads = useMemo(() => [...new Set((scans || []).map((s) => s.road).filter(Boolean))].sort(), [scans]);
  const SORT_GET = {
    road: (s) => (s.road || s.filename || "").toLowerCase(),
    damage: (s) => s.scan.damage_class,
    severity: (s) => s.scan.severity || 0,
    treatment: (s) => s.cost?.treatment_code || "",
    qty: (s) => s.cost?.quantity || 0,
    rate: (s) => s.cost?.rate || 0,
    cost: (s) => s.cost?.cost_ex_gst || 0,
    created_at: (s) => s.created_at || "",
  };
  const view = useMemo(() => {
    let v = scans || [];
    if (roadFilter) v = v.filter((s) => s.road === roadFilter);
    const get = SORT_GET[sort.key] || SORT_GET.created_at;
    return [...v].sort((a, b) => (get(a) > get(b) ? 1 : get(a) < get(b) ? -1 : 0) * sort.dir);
  }, [scans, roadFilter, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const onSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }));
  const allSel = view?.length && view.every((s) => selected.includes(s.id));

  if (!scans) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold-400" /></div>;

  return (
    <div className="space-y-6" data-testid="ew-prep-page">
      <div>
        <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2"><ScanSearch className="h-3.5 w-3.5" /> EW Prep · Evidence scanner</Mono>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1 className="font-heading text-3xl text-white mt-2">EW Evidence & Form Compiler</h1>
          <div className="flex items-center gap-2 mt-2">
            <button onClick={() => setVideoOpen((o) => !o)} data-testid="ew-watch-video-btn"
              className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-400 hover:bg-gold-500/10 px-4 py-2.5 text-sm transition-colors">
              <PlayCircle className="h-4 w-4" /> {videoOpen ? "Hide video" : "Watch how it works"}
            </button>
            <button onClick={() => setFcOpen(true)} data-testid="ew-import-fc-btn"
              className="inline-flex items-center gap-2 border border-gold-500/40 text-gold-400 hover:bg-gold-500/10 px-4 py-2.5 text-sm transition-colors">
              <Camera className="h-4 w-4" /> Import from FieldClaim
            </button>
          </div>
        </div>
        <p className="text-white/50 text-sm mt-2 max-w-3xl">
          {jur === "nsw"
            ? "Upload damage photos — the AI scanner classifies the damage, maps it to a NSW standard treatment with the regionally verified rate for your region (FY25/26), and compiles the NSWRA EW claim schedule. Review and override any classification or dimensions before compiling."
            : "Upload damage photos — the AI scanner classifies the damage, maps it to a regionally verified benchmark treatment, computes cost (incl. 10% EW uplift), and compiles the DTMR Emergency Works Claim Form 1. Review and override any classification or dimensions before compiling."}
        </p>
        <p className="text-white/45 text-xs mt-2 max-w-3xl" data-testid="ew-rdd-note">
          Detection engine: classes aligned to <span className="text-gold-400">RDD-2022</span> — the world-standard AI road damage taxonomy from the IEEE Global Road Damage Detection Challenge (D00 linear cracking, D20 fatigue cracking, D40 potholes) — extended with the disaster-recovery classes the standard doesn't cover. YOLO-family models trained on RDD datasets drop straight in, no API change.
        </p>
        <div className="inline-flex border border-gold-500/30 mt-4" data-testid="ew-jurisdiction-toggle">
          {[["qld", "QLD · DTMR"], ["nsw", "NSW · NSWRA"]].map(([k, l]) => (
            <button key={k} onClick={() => setJur(k)} data-testid={`ew-jur-${k}`}
              className={`px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors ${jur === k ? "bg-gold-500 text-navy-950 font-semibold" : "text-white/55 hover:text-gold-400"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {videoOpen && (
        <div className="border border-gold-500/20 bg-navy-900/40 p-5" data-testid="ew-video-panel">
          <Mono className="text-gold-400 text-[0.55rem] flex items-center gap-2 mb-3"><PlayCircle className="h-3.5 w-3.5" /> EW Prep walkthrough (2 min 18 s)</Mono>
          <video controls autoPlay preload="metadata" className="w-full max-w-4xl border border-gold-500/20 bg-black" data-testid="ew-video-player">
            <source src="/MDAu_EW_Prep_Walkthrough.mp4" type="video/mp4" />
          </video>
        </div>
      )}

      <UploadPanel onScanned={onScanned} jur={jur} region={region} setRegion={setRegion} regions={meta?.nsw?.regions || ["North Coast"]} />

      <RadarImportPanel jur={jur} region={region} onScanned={onScanned} />

      <div className="border border-gold-500/20 bg-navy-900/40" data-testid="ew-scans-table">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gold-500/15 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Mono className="text-gold-400 text-[0.55rem]">Scanned evidence · {view.length}{roadFilter ? ` of ${scans.length}` : ""}</Mono>
            {roads.length > 1 && (
              <select value={roadFilter} onChange={(e) => setRoadFilter(e.target.value)} data-testid="ew-road-filter"
                className="bg-navy-950 border border-gold-500/25 text-white/70 text-xs px-2 py-1">
                <option value="">All roads</option>
                {roads.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
          </div>
          <span className="font-mono text-xs text-gold-400" data-testid="ew-selected-total">Selected: {selected.length} · {money(total)}</span>
        </div>
        {view.length === 0 ? (
          <div className="p-10 text-center text-white/40 text-sm" data-testid="ew-empty">{roadFilter ? "No scans for this road." : "No scans yet — upload damage photos above to begin."}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-gold-500/15">
                  <th className="px-4 py-2"><input type="checkbox" checked={!!allSel} onChange={() => setSelected(allSel ? [] : view.map((s) => s.id))} data-testid="ew-select-all" /></th>
                  {[["Photo / Road", "road"], ["Damage class", "damage"], ["Severity", "severity"], ["Treatment", "treatment"], ["Qty", "qty"], ["Rate", "rate"], ["Cost ex GST", "cost"], ["", null]].map(([h, k]) => (
                    <th key={h || "act"} className={`px-4 py-2 ${k ? "cursor-pointer select-none" : ""}`} onClick={k ? () => onSort(k) : undefined} data-testid={k ? `ew-sort-${k}` : undefined}>
                      <Mono className={`text-[0.5rem] ${sort.key === k ? "text-gold-400" : "text-white/40"}`}>{h}{sort.key === k ? (sort.dir === 1 ? " ▲" : " ▼") : ""}</Mono>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {view.map((s) => (
                  editing === s.id ? (
                    <EditRow key={s.id} scan={s} classes={classes} onSave={onSaved} onCancel={() => setEditing(null)} />
                  ) : (
                    <tr key={s.id} className="border-b border-gold-500/10 hover:bg-navy-900/60" data-testid={`ew-scan-row-${s.id}`}>
                      <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleSel(s.id)} data-testid={`ew-select-${s.id}`} /></td>
                      <td className="px-4 py-3">
                        <div className="text-white text-xs font-medium">{s.filename}
                          {s.source === "fieldclaim" && <Mono className="text-[0.45rem] text-emerald-400 border border-emerald-400/40 px-1 py-0.5 ml-2" data-testid={`ew-fc-tag-${s.id}`}>FC</Mono>}
                        </div>
                        <div className="text-white/45 text-[0.65rem] mt-0.5">{s.road || "—"}{s.radar_ref ? ` · ${s.radar_ref}` : ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gold-400 text-xs">{s.scan.damage_class.replace(/_/g, " ")}</span>
                        <div className="text-white/40 text-[0.6rem] mt-0.5">
                          conf {Math.round((s.scan.confidence || 0) * 100)}%
                          {meta?.rdd_codes?.[s.scan.damage_class] && <span className="ml-1.5 border border-white/15 px-1 text-white/50" data-testid={`ew-rdd-${s.id}`}>RDD {meta.rdd_codes[s.scan.damage_class]}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs ${s.scan.severity >= 4 ? "text-red-400" : s.scan.severity === 3 ? "text-amber-300" : "text-white/60"}`}>
                          {s.scan.severity >= 4 && <AlertTriangle className="h-3 w-3 inline mr-1" />}{SEV[s.scan.severity] || s.scan.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/70 text-xs">
                        {s.cost?.treatment_code ? `${s.cost.treatment_code} — ${s.cost.treatment}` : "No costed treatment"}
                        {s.jurisdiction === "nsw" && <Mono className="text-[0.45rem] text-sky-300 border border-sky-400/40 px-1 py-0.5 ml-2" data-testid={`ew-nsw-tag-${s.id}`}>NSW</Mono>}
                      </td>
                      <td className="px-4 py-3 text-white/70 text-xs font-mono">{s.cost?.quantity || 0} {s.cost?.uom || ""}</td>
                      <td className="px-4 py-3 text-white/70 text-xs font-mono">{s.cost?.rate != null ? money(s.cost.rate) : (s.cost?.treatment_code ? "Rate on application" : "—")}</td>
                      <td className="px-4 py-3 text-gold-400 text-xs font-mono" data-testid={`ew-cost-${s.id}`}>{money(s.cost?.cost_ex_gst)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => setEditing(s.id)} data-testid={`ew-edit-btn-${s.id}`} className="text-white/40 hover:text-gold-400 mr-3"><Pencil className="h-3.5 w-3.5" /></button>
                        <button onClick={() => del(s.id)} data-testid={`ew-delete-btn-${s.id}`} className="text-white/40 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CompilePanel selected={selected} jur={jur} region={region} regions={meta?.nsw?.regions || []} />

      <SheetValidatePanel />

      {fcOpen && <ImportFCModal onClose={() => setFcOpen(false)} onScanned={onScanned} jur={jur} region={region} />}
    </div>
  );
}
